import React, { useState, useRef, useCallback, useEffect } from "react";
import { Paper, Fade, Portal, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Conversation, Message } from "@/types/chat";
import { useAuth } from "@/hooks/auth";
import { ChatWindowHeader } from "./ChatWindowHeader";
import { ChatMessageList } from "./ChatMessageList";
import { ChatInputArea } from "./ChatInputArea";
import { ChatActionMenu } from "./ChatActionMenu";
import { ShareRecordDialog } from "./ShareRecordDialog";
import { AddMemberDialog } from "./AddMemberDialog";
import { ManageGroupDialog } from "./ManageGroupDialog";

interface ChatWindowProps {
	conversation?: Conversation;
	messages: Message[];
	typingUsers: string[];
	onClose: () => void;
	onSendMessage: (
		conversationId: string,
		content: string,
		file: File | null,
		onProgress?: (p: number) => void,
	) => void;
	onReact?: (
		conversationId: string,
		messageId: string,
		emoji: string,
		action: "add" | "remove",
	) => void;
	onTyping: (conversationId: string, isTyping: boolean) => void;
	onLoadMore?: (conversationId: string) => void;
	onAddMembers?: (conversationId: string, participantIds: string[]) => void;
	onRemoveMember?: (conversationId: string, userId: string) => Promise<void>;
	onDeleteGroup?: (conversationId: string) => Promise<void>;
	hasMore?: boolean;
	isLoadingMore?: boolean;
	positionIndex?: number;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
	conversation,
	messages,
	typingUsers,
	onClose,
	onSendMessage,
	onReact,
	onTyping,
	onLoadMore,
	onAddMembers,
	onRemoveMember,
	onDeleteGroup,
	hasMore,
	isLoadingMore,
	positionIndex = 0,
}) => {
	const theme = useTheme();
	const { user } = useAuth();
	const [plusAnchorEl, setPlusAnchorEl] = useState<null | HTMLElement>(null);
	const [reactionAnchorEl, setReactionAnchorEl] = useState<null | HTMLElement>(
		null,
	);
	const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
	const [shareDialogOpen, setShareDialogOpen] = useState(false);
	const [shareRecordType, setShareRecordType] = useState<
		"customer" | "contract" | "invoice"
	>("customer");
	const [isMinimized, setIsMinimized] = useState(false);
	const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
	const [isManageGroupOpen, setIsManageGroupOpen] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const previousMessagesLength = React.useRef(messages.length);

	React.useEffect(() => {
		if (isMinimized && messages.length > previousMessagesLength.current) {
			const newMessages = messages.slice(previousMessagesLength.current);
			const unread = newMessages.filter((m) => m.senderId !== user?.id).length;
			setUnreadCount((prev) => prev + unread);
		}
		previousMessagesLength.current = messages.length;
	}, [messages, isMinimized, user?.id]);

	React.useEffect(() => {
		if (!isMinimized) {
			setUnreadCount(0);
			previousMessagesLength.current = messages.length;
		}
	}, [isMinimized, messages.length]);

	// Resizing logic
	const [dimensions, setDimensions] = useState({ width: 340, height: 480 });
	const isResizing = useRef(false);
	const startPos = useRef({ x: 0, y: 0, w: 0, h: 0 });

	const handleMouseMove = useCallback((e: MouseEvent) => {
		if (!isResizing.current) return;
		const dx = startPos.current.x - e.clientX;
		const dy = startPos.current.y - e.clientY;
		
		let newWidth = startPos.current.w + dx;
		let newHeight = startPos.current.h + dy;

		// constraints
		newWidth = Math.max(280, Math.min(newWidth, 600));
		newHeight = Math.max(350, Math.min(newHeight, 800));

		setDimensions({ width: newWidth, height: newHeight });
	}, []);

	const handleMouseUp = useCallback(() => {
		isResizing.current = false;
		document.removeEventListener("mousemove", handleMouseMove);
		document.removeEventListener("mouseup", handleMouseUp);
		// Enable text selection again
		document.body.style.userSelect = "auto";
	}, [handleMouseMove]);

	const handleMouseDown = (e: React.MouseEvent) => {
		isResizing.current = true;
		startPos.current = {
			x: e.clientX,
			y: e.clientY,
			w: dimensions.width,
			h: dimensions.height,
		};
		// Disable text selection while dragging
		document.body.style.userSelect = "none";
		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
	};

	useEffect(() => {
		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			document.body.style.userSelect = "auto";
		};
	}, [handleMouseMove, handleMouseUp]);

	if (!conversation) return null;

	const handleShareAction = (type: "customer" | "contract" | "invoice") => {
		setShareRecordType(type);
		setShareDialogOpen(true);
	};

	const handleShareRecordSelect = (id: string, label: string) => {
		if (conversation) {
			onSendMessage(
				conversation.id,
				`$$SHARE:${shareRecordType.toUpperCase()}:${id}$$`,
				null,
			);
		}
	};

	return (
		<Portal>
			<Fade in={Boolean(conversation)}>
				<Paper
					elevation={12}
					sx={{
						position: "fixed",
						bottom: 24,
						right: 24 + positionIndex * (dimensions.width + 20),
						width: dimensions.width,
						height: isMinimized ? "auto" : dimensions.height,
						zIndex: 1300, // Above normal UI, below some modals
						display: "flex",
						flexDirection: "column",
						borderRadius: 3,
						overflow: "hidden",
						backdropFilter: "blur(24px)",
						backgroundColor:
							theme.palette.mode === "dark"
								? "rgba(18, 18, 18, 0.85)"
								: "rgba(255, 255, 255, 0.9)",
						border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : theme.palette.divider}`,
						boxShadow:
							theme.palette.mode === "dark"
								? "0 8px 32px rgba(0,0,0,0.4)"
								: theme.shadows[12],
					}}
				>
					<ChatWindowHeader
						conversation={conversation}
						typingUsers={typingUsers}
						onClose={onClose}
						onMinimize={() => setIsMinimized(!isMinimized)}
						isMinimized={isMinimized}
						unreadCount={isMinimized ? unreadCount : 0}
						onAddMemberClick={() => setIsAddMemberOpen(true)}
						onManageGroupClick={() => setIsManageGroupOpen(true)}
						onDeleteChat={() => onDeleteGroup?.(conversation.id)}
					/>

					{/* Resize Handle */}
					{!isMinimized && (
						<Box
							onMouseDown={handleMouseDown}
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								width: 24,
								height: 24,
								cursor: "nwse-resize",
								zIndex: 1400,
								"&:hover": {
									"& .resize-icon": { opacity: 1 }
								}
							}}
						>
							<svg className="resize-icon" width="12" height="12" viewBox="0 0 12 12" style={{ position: "absolute", top: 4, left: 4, opacity: 0.3, transition: "opacity 0.2s" }}>
								<path d="M0,0 L12,0 L0,12 Z" fill={theme.palette.mode === 'dark' ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)"} />
							</svg>
						</Box>
					)}

					{!isMinimized && (
						<>
							<ChatMessageList
								conversation={conversation}
								messages={messages}
								user={user}
								typingUsers={typingUsers}
								isLoadingMore={isLoadingMore}
								hasMore={hasMore}
								onLoadMore={onLoadMore}
								onReact={onReact}
								reactionAnchorEl={reactionAnchorEl}
								setReactionAnchorEl={setReactionAnchorEl}
								activeMessageId={activeMessageId}
								setActiveMessageId={setActiveMessageId}
							/>

							<ChatInputArea
								conversation={conversation}
								onSendMessage={onSendMessage}
								onTyping={onTyping}
								onPlusClick={(e) => setPlusAnchorEl(e.currentTarget)}
							/>
						</>
					)}
				</Paper>
			</Fade>

			<ChatActionMenu
				anchorEl={plusAnchorEl}
				onClose={() => setPlusAnchorEl(null)}
				onShareAction={handleShareAction}
			/>

			<ShareRecordDialog
				open={shareDialogOpen}
				onClose={() => setShareDialogOpen(false)}
				type={shareRecordType}
				onSelect={handleShareRecordSelect}
			/>

			{onAddMembers && (
				<AddMemberDialog
					open={isAddMemberOpen}
					onClose={() => setIsAddMemberOpen(false)}
					existingMemberIds={conversation.participants?.map((p) => p.id) || []}
					onAddMembers={(ids) => {
						onAddMembers(conversation.id, ids);
					}}
				/>
			)}

			{onRemoveMember && onDeleteGroup && (
				<ManageGroupDialog
					open={isManageGroupOpen}
					onClose={() => setIsManageGroupOpen(false)}
					conversation={conversation}
					onRemoveMember={(userId) => onRemoveMember(conversation.id, userId)}
					onDeleteGroup={() => onDeleteGroup(conversation.id)}
				/>
			)}
		</Portal>
	);
};
